import exportFromJSON from "export-from-json";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export const excelParser = () => {
  // Keep your existing function for CSV/JSON/HTML exports
  function exportDataFromJSON(data, newFileName, fileExportType) {
    if (!data) return;
    try {
      const fileName = newFileName || "exported-data";
      const exportType = exportFromJSON.types[fileExportType || "xls"];
      exportFromJSON({ data, fileName, exportType });
    } catch (e) {
      throw new Error("Parsing failed!");
    }
  }

  // True Excel export function (.xlsx format - no warnings!)
  function exportToTrueExcel(data, newFileName, sheetName = "Sheet1") {
    if (!data || !data.length) {
      throw new Error("No data provided for Excel export!");
    }

    try {
      const fileName = newFileName || "exported-data";
      
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (e) {
      throw new Error(`Excel export failed: ${e.message}`);
    }
  }

  // Optional: Advanced Excel export with multiple sheets
  function exportToTrueExcelMultiSheet(sheets, newFileName) {
    if (!sheets || !sheets.length) {
      throw new Error("No sheets data provided for Excel export!");
    }

    try {
      const fileName = newFileName || "exported-data";
      const workbook = XLSX.utils.book_new();
      
      sheets.forEach(sheet => {
        const worksheet = XLSX.utils.json_to_sheet(sheet.data);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
      });
      
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (e) {
      throw new Error(`Multi-sheet Excel export failed: ${e.message}`);
    }
  }

  // PDF export function - SIMPLIFIED & WORKING
  function exportToPDF(data, newFileName, options = {}) {
    if (!data || !data.length) {
      throw new Error("No data provided for PDF export!");
    }

    try {
      const fileName = newFileName || "exported-data";
      
      // Simple initialization
      const doc = new jsPDF();
      
      // Prepare table headers and rows
      const headers = Object.keys(data[0]);
      const rows = data.map(item => headers.map(header => item[header]));
      
      let currentY = 20;
      
      // Add title if provided
      if (options.title) {
        doc.setFontSize(16);
        doc.text(options.title, 14, currentY);
        currentY += 10;
      }
      
      // Add company name if provided
      if (options.companyName) {
        doc.setFontSize(12);
        doc.text(options.companyName, 14, currentY);
        currentY += 8;
      }
      
      // Add date if showDate is true
      if (options.showDate) {
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, currentY);
        currentY += 8;
      }
      
      // Generate the table
      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: currentY,
        theme: options.theme || 'striped',
        styles: {
          fontSize: options.fontSize || 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: options.headerColor || [41, 128, 185],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: options.rowStriping !== false ? {
          fillColor: [245, 245, 245]
        } : undefined,
        margin: { top: 20, bottom: 20, left: 10, right: 10 }
      });
      
      // Add simple footer if provided
      if (options.footerText) {
        const finalY = doc.lastAutoTable.finalY;
        doc.setFontSize(8);
        doc.setTextColor([100, 100, 100]);
        doc.text(options.footerText, 14, finalY + 10);
      }
      
      // Save the PDF
      doc.save(`${fileName}.pdf`);
      
    } catch (e) {
      console.error('PDF Error:', e);
      throw new Error(`PDF export failed: ${e.message}`);
    }
  }

  // Export HTML content to PDF
  function exportHTMLToPDF(htmlContent, newFileName, options = {}) {
    if (!htmlContent) {
      throw new Error("No HTML content provided!");
    }

    try {
      const fileName = newFileName || "exported-data";
      const doc = new jsPDF(options.pdfOptions || {});
      
      doc.html(htmlContent, {
        callback: function(doc) {
          doc.save(`${fileName}.pdf`);
        },
        x: options.x || 10,
        y: options.y || 10,
        html2canvas: options.html2canvasOptions || { scale: 0.5 },
        autoPaging: options.autoPaging || 'text'
      });
    } catch (e) {
      throw new Error(`HTML to PDF export failed: ${e.message}`);
    }
  }

  // Print function - opens browser print dialog
  function printData(data, options = {}) {
    if (!data || !data.length) {
      throw new Error("No data provided for printing!");
    }

    try {
      const printWindow = window.open('', '_blank');
      
      const headers = Object.keys(data[0]);
      const rows = data.map(item => headers.map(header => item[header]));
      
      const title = options.title || 'Data Report';
      const companyName = options.companyName || '';
      const showDate = options.showDate !== false;
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body {
              font-family: ${options.fontFamily || 'Arial, sans-serif'};
              margin: 20px;
              padding: 0;
            }
            .print-container {
              max-width: ${options.maxWidth || '100%'};
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .company-name {
              font-size: ${options.companyFontSize || '20px'};
              font-weight: bold;
              margin-bottom: 10px;
            }
            .title {
              font-size: ${options.titleFontSize || '18px'};
              font-weight: bold;
              margin-bottom: 10px;
            }
            .date {
              font-size: ${options.dateFontSize || '12px'};
              color: #666;
              margin-top: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: ${options.cellPadding || '8px'};
              text-align: left;
            }
            th {
              background-color: ${options.headerBgColor || '#f2f2f2'};
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: ${options.rowStripeColor || '#f9f9f9'};
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              font-size: ${options.footerFontSize || '10px'};
              color: #666;
            }
            @media print {
              body {
                margin: 0;
                padding: 10px;
              }
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="header">
              ${companyName ? `<div class="company-name">${companyName}</div>` : ''}
              <div class="title">${title}</div>
              ${showDate ? `<div class="date">Printed: ${new Date().toLocaleString()}</div>` : ''}
            </div>
            
            <table>
              <thead>
                <tr>
                  ${headers.map(header => `<th>${header}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${rows.map(row => `
                  <tr>
                    ${row.map(cell => `<td>${cell !== undefined && cell !== null ? cell : ''}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="footer">
              ${options.footer || 'Generated by System'}
            </div>
          </div>
          
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print(); setTimeout(() => window.close(), 100);" style="padding: 10px 20px; margin: 0 10px; cursor: pointer;">
              🖨️ Print
            </button>
            <button onclick="window.close();" style="padding: 10px 20px; margin: 0 10px; cursor: pointer;">
              Close
            </button>
          </div>
          
          <script>
            ${options.autoPrint !== false ? 'window.onload = () => { setTimeout(() => window.print(), 500); }' : ''}
          <\/script>
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      
    } catch (e) {
      throw new Error(`Print failed: ${e.message}`);
    }
  }

  // Print HTML content directly
  function printHTML(htmlContent, options = {}) {
    if (!htmlContent) {
      throw new Error("No HTML content provided for printing!");
    }

    try {
      const printWindow = window.open('', '_blank');
      
      const fullHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${options.title || 'Print Document'}</title>
          <style>
            ${options.customStyles || ''}
            @media print {
              body {
                margin: 0;
                padding: 10px;
              }
            }
          </style>
        </head>
        <body>
          ${htmlContent}
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print(); setTimeout(() => window.close(), 100);" style="padding: 10px 20px; margin: 0 10px;">
              🖨️ Print
            </button>
            <button onclick="window.close();" style="padding: 10px 20px; margin: 0 10px;">
              Close
            </button>
          </div>
          ${options.autoPrint !== false ? '<script>window.onload = () => { setTimeout(() => window.print(), 500); }<\/script>' : ''}
        </body>
        </html>
      `;
      
      printWindow.document.write(fullHtml);
      printWindow.document.close();
      printWindow.focus();
      
    } catch (e) {
      throw new Error(`HTML print failed: ${e.message}`);
    }
  }

  // Print from an existing DOM element
  function printElement(elementId, options = {}) {
    if (!elementId) {
      throw new Error("No element ID provided for printing!");
    }

    try {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error(`Element with ID "${elementId}" not found!`);
      }
      
      const printWindow = window.open('', '_blank');
      const originalContent = element.cloneNode(true);
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${options.title || 'Print Document'}</title>
          <style>
            ${options.customStyles || ''}
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
            }
            @media print {
              body {
                margin: 0;
                padding: 10px;
              }
            }
          </style>
        </head>
        <body>
          ${originalContent.outerHTML}
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print(); setTimeout(() => window.close(), 100);" style="padding: 10px 20px;">
              🖨️ Print
            </button>
            <button onclick="window.close();" style="padding: 10px 20px;">
              Close
            </button>
          </div>
          ${options.autoPrint !== false ? '<script>window.onload = () => { setTimeout(() => window.print(), 500); }<\/script>' : ''}
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      
    } catch (e) {
      throw new Error(`Element print failed: ${e.message}`);
    }
  }

  return {
    exportDataFromJSON,
    exportToTrueExcel,
    exportToTrueExcelMultiSheet,
    exportToPDF,        // Now working!
    exportHTMLToPDF,
    printData,
    printHTML,
    printElement
  };
};